import { useCallback, useEffect, useRef, useState } from 'react';
import { isForbidden, organisationErrorMessage } from '../../organisations/lib/errorMessage';

const LOADING = { status: 'loading', data: null, error: '' };

/**
 * One Activity section's request: `fetcher(studentId, period)`, run when
 * `enabled`, again for each new student or period, and again on `retry`.
 *
 * Returns { status, data, error, retry } where status is 'loading',
 * 'ready', 'empty' (ready, and `isEmpty(data)` said there is nothing to
 * show), 'forbidden' (403) or 'error' (with a message for people).
 *
 * A result is kept with the student, period and attempt it answers, so a
 * new period reads as loading at once rather than showing the old period's
 * numbers under the new label, and an answer already held for the same
 * inputs is kept rather than fetched again. Requests are not aborted
 * (APIService replaces any AbortController signal with its own); a
 * `cancelled` flag drops a response that arrives after the inputs changed.
 */
export function useActivitySection(fetcher, studentId, period, { enabled = true, isEmpty } = {}) {
  const key = `${studentId}|${period}`;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ key: null, attempt: -1, ...LOADING });
  const isEmptyRef = useRef(isEmpty);
  isEmptyRef.current = isEmpty;
  const resultRef = useRef(result);
  resultRef.current = result;

  useEffect(() => {
    if (!enabled) return undefined;
    // Already answered for these inputs (a section keyed independently of
    // the period, re-enabled after the summary reloads): keep that answer.
    if (resultRef.current.key === key && resultRef.current.attempt === attempt) return undefined;
    let cancelled = false;
    const settle = (state) => { if (!cancelled) setResult({ key, attempt, ...state }); };
    fetcher(studentId, period)
      .then((data) => settle({
        status: isEmptyRef.current && isEmptyRef.current(data) ? 'empty' : 'ready',
        data,
        error: '',
      }))
      .catch((err) => settle(isForbidden(err)
        ? { status: 'forbidden', data: null, error: '' }
        : { status: 'error', data: null, error: organisationErrorMessage(err, 'Please try again.') }));
    return () => { cancelled = true; };
  }, [fetcher, studentId, period, key, attempt, enabled]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const current = result.key === key && result.attempt === attempt;
  const { status, data, error } = current ? result : LOADING;
  return { status, data, error, retry };
}
